import type React from 'react';
import { useEffect } from 'react';
import { reportError } from 'utils/sentry.utils';

// Injects a nonce into style tags that are missing one, to help with some inline styles we cannot change
// to support having our CSP be without style-src: 'unsafe-inline'
const ContentSecurityPolicyStyleInjector: React.FC = () => {
  useEffect(() => {
    if (!window.nonce) {
      // Report an error to sentry
      console.error('CSP Style Monitor: window.nonce is not defined. Style tags will not be fixed.');
      reportError(
        'ContentSecurityPolicyStyleInjector',
        new Error('window.nonce is not defined. Styles might appear broken in the app.')
      );
      return;
    }

    const copyStyleTag = (oldStyle: HTMLStyleElement) => {
      const newStyle = document.createElement('style');

      // Copy all the attributes
      for (const attribute of oldStyle.attributes) {
        newStyle.setAttribute(attribute.name, attribute.value);
      }

      // Copy all the CSS rules - if they are inline in the HTML
      newStyle.innerHTML = oldStyle.innerHTML;

      return newStyle;
    };

    // Recreates the style tag in the same position, with the page nonce
    const recreateStyleWithNonce = (oldStyle: HTMLStyleElement) => {
      const newStyle = copyStyleTag(oldStyle);
      newStyle.nonce = window.nonce;

      oldStyle.parentNode?.insertBefore(newStyle, oldStyle.nextSibling);

      // Copy all the CSS rules - if they are injected directly to sheet.cssRules
      // Has to be done only after the newStyle is inserted to the DOM
      const cssRules = oldStyle.sheet?.cssRules || [];
      for (const rule of cssRules) {
        newStyle?.sheet?.insertRule(rule.cssText, newStyle?.sheet?.cssRules?.length);
      }

      oldStyle.remove();
    };

    // Initial hydration - In case somehow the page already has inline style tags without nonce
    const hydrateStyleTags = () => {
      const styleTags = document.head.querySelectorAll<HTMLStyleElement>('style:not([nonce])');

      styleTags.forEach(recreateStyleWithNonce);
    };

    // Detect additions of style tags and recreate with nonce if needed
    const styleTagMutationCallback = (mutations: MutationRecord[]) => {
      mutations
        .filter(mutation => mutation.type === 'childList')
        .forEach(mutation => {
          Array.from(mutation.addedNodes)
            .filter(
              node =>
                node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'STYLE' && !(node as HTMLStyleElement).nonce
            )
            .forEach(node => recreateStyleWithNonce(node as HTMLStyleElement));
        });
    };

    const styleTagObserver = new MutationObserver(styleTagMutationCallback);

    // Observe changes in nodes that are direct children of <head>
    styleTagObserver.observe(document.head, {
      childList: true,
      subtree: false
    });

    hydrateStyleTags();

    return () => {
      styleTagObserver.disconnect();
    };
  }, []);

  return null;
};

export default ContentSecurityPolicyStyleInjector;
